import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { obtenerEstadisticasVentas } from '../api/backend_v2';
import { formatearMoneda } from '../utils/moneda';
import {
  ChartBarIcon,
  CalendarIcon,
  BeakerIcon,
  EyeIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  SparklesIcon,
  TagIcon,
  BuildingStorefrontIcon,
  ChevronRightIcon,
  FireIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline';

const EstadisticasVentas = () => {
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState('mes_actual');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabVisionActiva, setTabVisionActiva] = useState('monofocal');

  // Calcular fechas según período rápida
  const resolverFechasPorPeriodo = useCallback((opcion) => {
    const hoy = new Date();
    const yyyy = hoy.getFullYear();
    const mm = String(hoy.getMonth() + 1).padStart(2, '0');

    if (opcion === 'mes_actual') {
      const desde = `${yyyy}-${mm}-01`;
      const ddHoy = String(hoy.getDate()).padStart(2, '0');
      const hasta = `${yyyy}-${mm}-${ddHoy}`;
      return { desde, hasta };
    }

    if (opcion === 'mes_anterior') {
      const fechaMesPasado = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
      const ultDiaMesPasado = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
      const mmPasado = String(fechaMesPasado.getMonth() + 1).padStart(2, '0');
      const yyyyPasado = fechaMesPasado.getFullYear();
      const desde = `${yyyyPasado}-${mmPasado}-01`;
      const hasta = `${yyyyPasado}-${mmPasado}-${String(ultDiaMesPasado.getDate()).padStart(2, '0')}`;
      return { desde, hasta };
    }

    if (opcion === 'ultimos_3_meses') {
      const fechaHace3 = new Date(hoy.getFullYear(), hoy.getMonth() - 2, 1);
      const mmHace3 = String(fechaHace3.getMonth() + 1).padStart(2, '0');
      const yyyyHace3 = fechaHace3.getFullYear();
      const ddHoy = String(hoy.getDate()).padStart(2, '0');
      const desde = `${yyyyHace3}-${mmHace3}-01`;
      const hasta = `${yyyy}-${mm}-${ddHoy}`;
      return { desde, hasta };
    }

    if (opcion === 'anio_actual') {
      const ddHoy = String(hoy.getDate()).padStart(2, '0');
      const desde = `${yyyy}-01-01`;
      const hasta = `${yyyy}-${mm}-${ddHoy}`;
      return { desde, hasta };
    }

    return { desde: fechaDesde, hasta: fechaHasta };
  }, [fechaDesde, fechaHasta]);

  // Cargar datos del backend
  const cargarEstadisticas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { desde, hasta } = resolverFechasPorPeriodo(periodoSeleccionado);
      const res = await obtenerEstadisticasVentas({ fechaDesde: desde, fechaHasta: hasta });
      setDatos(res);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
      setError(err.message || 'No se pudieron cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  }, [periodoSeleccionado, resolverFechasPorPeriodo]);

  useEffect(() => {
    cargarEstadisticas();
  }, [cargarEstadisticas]);

  // Colores dinámicos para tipos de visión
  const getTipoVisionColor = (tipo) => {
    const t = (tipo || '').toLowerCase();
    if (t.includes('monofocal')) return { bar: 'bg-blue-600', badge: 'bg-blue-50 text-blue-700 border-blue-200' };
    if (t.includes('multi')) return { bar: 'bg-indigo-600', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200' };
    if (t.includes('ocupa')) return { bar: 'bg-emerald-600', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    if (t.includes('bifocal')) return { bar: 'bg-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' };
    return { bar: 'bg-purple-600', badge: 'bg-purple-50 text-purple-700 border-purple-200' };
  };

  // Colores dinámicos para rangos de precio
  const getRangoPrecioColor = (idx) => {
    const colores = [
      { bg: 'bg-emerald-50 border-emerald-200 text-emerald-900', bar: 'bg-emerald-500' },
      { bg: 'bg-blue-50 border-blue-200 text-blue-900', bar: 'bg-blue-500' },
      { bg: 'bg-indigo-50 border-indigo-200 text-indigo-900', bar: 'bg-indigo-500' },
      { bg: 'bg-amber-50 border-amber-200 text-amber-900', bar: 'bg-amber-500' },
    ];
    return colores[idx % colores.length];
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 sm:p-6 lg:p-8 space-y-6">
      {/* ── ENCABEZADO Y BARRA DE FILTROS ────────────────────────────────────── */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-gray-900 flex items-center gap-2 tracking-tight">
            <ChartBarIcon className="h-7 w-7 text-blue-600" />
            Estadísticas y Análisis de Ventas
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Consulta el rendimiento comercial, desgloses por tipos de cristales, laboratorios, lentes de contacto, marcas y rangos de precio.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Botones de Selección Rápida de Período */}
          <div className="flex bg-gray-100 p-1 rounded-xl text-xs font-semibold">
            {[
              { id: 'mes_actual', label: 'Este Mes' },
              { id: 'mes_anterior', label: 'Mes Anterior' },
              { id: 'ultimos_3_meses', label: 'Últimos 3 Meses' },
              { id: 'anio_actual', label: 'Año Actual' },
              { id: 'personalizado', label: 'Personalizado' },
            ].map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPeriodoSeleccionado(p.id)}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  periodoSeleccionado === p.id ? 'bg-white text-blue-600 shadow-xs' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={cargarEstadisticas}
            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
            title="Recargar datos"
          >
            <ArrowPathIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Rango de fechas si es personalizado */}
      {periodoSeleccionado === 'personalizado' && (
        <div className="bg-blue-50/60 border border-blue-100 p-4 rounded-xl flex flex-wrap items-center gap-4 text-xs">
          <span className="font-semibold text-blue-900 flex items-center gap-1">
            <CalendarIcon className="h-4 w-4 text-blue-600" /> Definir Rango de Fechas:
          </span>
          <div className="flex items-center gap-2">
            <label className="text-gray-600">Desde:</label>
            <input
              type="date"
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-blue-500"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-gray-600">Hasta:</label>
            <input
              type="date"
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-blue-500"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={cargarEstadisticas}
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors"
          >
            Aplicar Rango
          </button>
        </div>
      )}

      {/* Mensaje de Carga / Error */}
      {loading && (
        <div className="bg-white p-12 rounded-2xl shadow-sm border border-gray-100 text-center space-y-3">
          <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin mx-auto" />
          <p className="text-sm text-gray-600 font-medium">Calculando estadísticas y agregaciones de ventas...</p>
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm">
          <p className="font-bold">Error al obtener métricas</p>
          <p className="text-xs">{error}</p>
        </div>
      )}

      {!loading && !error && datos && (
        <>
          {/* ── TARJETAS DE KPIS SUPERIORES ──────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Ingresos</p>
                <p className="text-2xl font-black text-gray-900 mt-1">{formatearMoneda(datos.kpis.total_ingresos)}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">{datos.kpis.total_operaciones} operaciones</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600">
                <CurrencyDollarIcon className="h-6 w-6" />
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Cristales</p>
                <p className="text-2xl font-black text-blue-600 mt-1">{datos.kpis.total_cristales_vendidos}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Lentes vendidas</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-xl text-blue-600">
                <BeakerIcon className="h-6 w-6" />
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Armazones</p>
                <p className="text-2xl font-black text-indigo-600 mt-1">{datos.kpis.total_armazones_vendidos}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Monturas vendidas</p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600">
                <EyeIcon className="h-6 w-6" />
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Lentes de Contacto</p>
                <p className="text-2xl font-black text-purple-600 mt-1">{datos.kpis.total_lentes_contacto_vendidas || 0}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Unidades de LC</p>
              </div>
              <div className="p-3 bg-purple-50 rounded-xl text-purple-600">
                <SparklesIcon className="h-6 w-6" />
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-xs flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Ticket Promedio</p>
                <p className="text-2xl font-black text-amber-600 mt-1">{formatearMoneda(datos.kpis.ticket_promedio)}</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Promedio por venta</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-xl text-amber-600">
                <TagIcon className="h-6 w-6" />
              </div>
            </div>
          </div>

          {/* ── SECCIÓN 1: ANÁLISIS DE CRISTALES ──────────────────────────────── */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
              <BeakerIcon className="h-6 w-6 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-900">Análisis y Estadísticas de Cristales</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Cristales por Tipo de Visión */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center justify-between">
                  <span>Ventas por Tipo de Visión</span>
                  <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">
                    {datos.cristales.total_vendidos} cristales
                  </span>
                </h3>

                {datos.cristales.por_tipo_vision.length === 0 ? (
                  <p className="text-xs text-gray-500 py-6 text-center">No hay registros de cristales en este período</p>
                ) : (
                  <div className="space-y-3">
                    {datos.cristales.por_tipo_vision.map((item) => {
                      const styleColor = getTipoVisionColor(item.tipo);
                      return (
                        <div key={item.tipo} className="space-y-1.5">
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-bold text-gray-800 flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${styleColor.badge}`}>
                                {item.tipo}
                              </span>
                            </span>
                            <span className="font-mono font-bold text-gray-900">
                              {item.cantidad} u. <span className="text-gray-400 font-normal">({item.porcentaje}%)</span>
                            </span>
                          </div>
                          <div className="w-full bg-gray-100 h-2.5 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all duration-500 ${styleColor.bar}`}
                              style={{ width: `${Math.max(item.porcentaje, 4)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Cristales por Laboratorio / Proveedor */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center justify-between">
                  <span>Ventas por Laboratorio</span>
                  <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
                    Proveedores
                  </span>
                </h3>

                {(!datos.cristales.por_laboratorio || datos.cristales.por_laboratorio.length === 0) ? (
                  <p className="text-xs text-gray-500 py-6 text-center">No hay desglose por laboratorio</p>
                ) : (
                  <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                    {datos.cristales.por_laboratorio.map((lab) => (
                      <div key={lab.laboratorio} className="space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-bold text-gray-800 truncate" title={lab.laboratorio}>
                            🔬 {lab.laboratorio}
                          </span>
                          <span className="font-mono font-bold text-gray-900">
                            {lab.cantidad} u. <span className="text-gray-400 text-[11px]">({lab.porcentaje}%)</span>
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                          <div
                            className="bg-indigo-600 h-full transition-all duration-500"
                            style={{ width: `${Math.max(lab.porcentaje, 4)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Stock vs Laboratorio */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4 flex flex-col justify-between">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                    <BuildingStorefrontIcon className="h-4 w-4 text-emerald-600" />
                    Origen: Stock vs. Laboratorio
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Clasificación de cristales provistos directamente de stock o requeridos a laboratorio.
                  </p>

                  <div className="mt-4 space-y-3">
                    {datos.cristales.origen_stock_vs_laboratorio.map((orig) => {
                      const esStock = orig.origen === 'Stock';
                      return (
                        <div
                          key={orig.origen}
                          className={`p-3.5 rounded-xl border flex items-center justify-between ${
                            esStock ? 'bg-emerald-50/70 border-emerald-200' : 'bg-blue-50/70 border-blue-200'
                          }`}
                        >
                          <div>
                            <p className={`text-xs font-bold ${esStock ? 'text-emerald-900' : 'text-blue-900'}`}>
                              {esStock ? '📦 Cristales de Stock' : '🔬 Pedidos a Laboratorio'}
                            </p>
                            <p className="text-[11px] text-gray-600 mt-0.5">{orig.porcentaje}% del total de cristales</p>
                          </div>
                          <span className={`text-xl font-black ${esStock ? 'text-emerald-700' : 'text-blue-700'}`}>
                            {orig.cantidad}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="text-[11px] text-gray-400 bg-gray-50 p-2.5 rounded-lg border border-gray-200 mt-2">
                  ℹ️ Regla operativa: <strong>Stock</strong> (disponibilidad inmediata) | <strong>Laboratorio</strong> (receta/pedido especial).
                </div>
              </div>
            </div>

            {/* Desglose de Top Cristales por Tipo de Visión (Pestañas/Tabs) */}
            {datos.cristales.top_por_tipo_vision && (
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-3">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                    <TrophyIcon className="h-5 w-5 text-amber-500" />
                    Top Cristales Más Vendidos por Tipo de Visión
                  </h3>

                  {/* Tabs Selector de Visión */}
                  <div className="flex bg-gray-100 p-1 rounded-xl text-xs font-semibold self-start sm:self-auto">
                    {[
                      { id: 'monofocal', label: 'Monofocales' },
                      { id: 'ocupacional', label: 'Ocupacionales' },
                      { id: 'multifocal', label: 'Multifocales' },
                      { id: 'bifocal', label: 'Bifocales' },
                    ].map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setTabVisionActiva(t.id)}
                        className={`px-3 py-1.5 rounded-lg transition-all ${
                          tabVisionActiva === t.id ? 'bg-white text-blue-600 shadow-xs' : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Lista de Cristales para la Pestaña Activa */}
                {(!datos.cristales.top_por_tipo_vision[tabVisionActiva] || datos.cristales.top_por_tipo_vision[tabVisionActiva].length === 0) ? (
                  <p className="text-xs text-gray-500 py-6 text-center">No hay cristales registrados para este tipo de visión en el período</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    {datos.cristales.top_por_tipo_vision[tabVisionActiva].map((c, idx) => (
                      <div
                        key={idx}
                        className="p-3.5 bg-gray-50/80 border border-gray-200 rounded-xl flex items-start gap-3 relative overflow-hidden"
                      >
                        <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-amber-100 text-amber-800 font-bold text-xs flex items-center justify-center border border-amber-300">
                          #{idx + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-gray-900 truncate" title={c.cristal}>
                            {c.cristal}
                          </p>
                          <p className="text-[11px] font-semibold text-blue-600 mt-1">{c.cantidad} unidades</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── SECCIÓN 2: LENTES DE CONTACTO ─────────────────────────────────── */}
          {datos.lentes_contacto && (
            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
                <SparklesIcon className="h-6 w-6 text-purple-600" />
                <h2 className="text-lg font-bold text-gray-900">Análisis de Lentes de Contacto</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4 flex flex-col justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                      Resumen Lentes de Contacto
                    </h3>
                    <div className="mt-4 space-y-3">
                      <div className="p-4 bg-purple-50 rounded-xl border border-purple-200 flex justify-between items-center">
                        <div>
                          <p className="text-xs font-semibold text-purple-800">Unidades Vendidas</p>
                          <p className="text-2xl font-black text-purple-900 mt-0.5">{datos.lentes_contacto.total_vendidos}</p>
                        </div>
                        <SparklesIcon className="h-8 w-8 text-purple-600 opacity-80" />
                      </div>

                      <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 flex justify-between items-center">
                        <div>
                          <p className="text-xs font-semibold text-emerald-800">Recaudación LC</p>
                          <p className="text-xl font-black text-emerald-900 mt-0.5">{formatearMoneda(datos.lentes_contacto.recaudacion)}</p>
                        </div>
                        <CurrencyDollarIcon className="h-8 w-8 text-emerald-600 opacity-80" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4 lg:col-span-2">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
                    Ventas por Marca de Lentes de Contacto
                  </h3>

                  {(!datos.lentes_contacto.por_marca || datos.lentes_contacto.por_marca.length === 0) ? (
                    <p className="text-xs text-gray-500 py-6 text-center">No hay datos de lentes de contacto vendidas en este período</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {datos.lentes_contacto.por_marca.map((m, idx) => (
                        <div key={m.marca} className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 bg-purple-100 text-purple-800 text-xs font-bold rounded-lg flex items-center justify-center">
                              #{idx + 1}
                            </span>
                            <span className="text-xs font-bold text-gray-900">{m.marca}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-xs font-mono font-bold text-purple-700">{m.cantidad} u.</span>
                            <span className="text-[10px] text-gray-400 block">({m.porcentaje}%)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── SECCIÓN 3: ANÁLISIS DE ARMAZONES ─────────────────────────────── */}
          <div className="space-y-4 pt-4">
            <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
              <EyeIcon className="h-6 w-6 text-indigo-600" />
              <h2 className="text-lg font-bold text-gray-900">Análisis y Estadísticas de Armazones</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Armazones por Marca */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center justify-between">
                  <span>Marcas Más Vendidas</span>
                  <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
                    Top 10 marcas
                  </span>
                </h3>

                {datos.armazones.por_marca.length === 0 ? (
                  <p className="text-xs text-gray-500 py-6 text-center">No hay datos de armazones vendidos</p>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                    {datos.armazones.por_marca.map((m, idx) => (
                      <div key={m.marca} className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-bold text-gray-900 flex items-center gap-2">
                            <span className="w-5 h-5 bg-indigo-100 text-indigo-800 text-[10px] font-bold rounded-md flex items-center justify-center">
                              #{idx + 1}
                            </span>
                            {m.marca}
                          </span>
                          <div className="text-right">
                            <span className="font-bold text-gray-900 font-mono">{m.cantidad} u.</span>
                            <span className="text-gray-400 text-[11px] ml-1.5">({m.porcentaje}%)</span>
                          </div>
                        </div>

                        <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                          <div
                            className="bg-indigo-600 h-full transition-all duration-500"
                            style={{ width: `${Math.max(m.porcentaje, 5)}%` }}
                          />
                        </div>

                        {m.recaudacion > 0 && (
                          <p className="text-[11px] text-emerald-700 font-semibold text-right">
                            Recaudación: {formatearMoneda(m.recaudacion)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>


              {/* Armazones por Rangos de Precio */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                  <TagIcon className="h-4 w-4 text-emerald-600" />
                  Distribución por Rangos de Precio
                </h3>
                <p className="text-xs text-gray-500">
                  Agrupación de armazones vendidos según su categoría de precio al público.
                </p>

                {datos.armazones.por_rango_precio.length === 0 ? (
                  <p className="text-xs text-gray-500 py-6 text-center">Sin datos de precios de armazones</p>
                ) : (
                  <div className="space-y-3">
                    {datos.armazones.por_rango_precio.map((r, idx) => {
                      const color = getRangoPrecioColor(idx);
                      return (
                        <div key={r.rango} className={`p-4 rounded-xl border ${color.bg} space-y-2`}>
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-bold text-gray-900">{r.rango}</span>
                            <span className="font-mono font-bold text-sm">
                              {r.cantidad} u. <span className="text-xs font-normal opacity-80">({r.porcentaje}%)</span>
                            </span>
                          </div>

                          <div className="w-full bg-white/70 h-2 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all duration-500 ${color.bar}`}
                              style={{ width: `${Math.max(r.porcentaje, 4)}%` }}
                            />
                          </div>

                          {r.subtotal > 0 && (
                            <p className="text-[11px] text-right font-semibold opacity-90">
                              Subtotal: {formatearMoneda(r.subtotal)}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Top Modelos de Armazones */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                <FireIcon className="h-5 w-5 text-orange-500" />
                Modelos de Armazones Más Vendidos
              </h3>

              {datos.armazones.top_modelos.length === 0 ? (
                <p className="text-xs text-gray-500 py-4 text-center">No hay datos de modelos específicos</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {datos.armazones.top_modelos.map((m, idx) => (
                    <div key={idx} className="p-3.5 bg-gray-50 border border-gray-200 rounded-xl space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-indigo-900">{m.marca}</span>
                        <span className="text-[10px] font-bold bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded">
                          #{idx + 1}
                        </span>
                      </div>
                      <p className="text-xs font-medium text-gray-700 truncate font-mono">Mod. {m.modelo}</p>
                      <div className="flex justify-between items-center text-[11px] pt-1 text-gray-500">
                        <span>{m.cantidad} unidades</span>
                        {m.precio_promedio > 0 && (
                          <span className="font-bold text-gray-900">{formatearMoneda(m.precio_promedio)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EstadisticasVentas;
